// Cross-Platform Sentiment Comparison Component
import { useState, useEffect } from "react";
import axios from "axios";
import { Globe, BarChart3, MessageSquare, Loader2, Sparkles, Wand2, CheckCircle2, Link2, ArrowLeftRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { API, PLATFORM_COLORS, getPlatformBadgeClass } from "./constants";

const SENTIMENT_COLORS = {
  positive: "#10B981",
  neutral: "#6B7280",
  negative: "#EF4444",
};

const PLATFORM_OPTIONS = [
  { value: "youtube", label: "YouTube" },
  { value: "tiktok", label: "TikTok" },
  { value: "instagram", label: "Instagram" },
  { value: "bilibili", label: "Bilibili" },
  { value: "douyin", label: "Douyin" },
  { value: "xiaohongshu", label: "Xiaohongshu" },
  { value: "weibo", label: "Weibo" },
];

const T = {
  ko: {
    title: "Cross-Platform 비교 분석",
    subtitle: "서로 다른 플랫폼에 올린 유사 게시물의 댓글 반응을 비교합니다",
    platformA: "플랫폼 A",
    platformB: "플랫폼 B",
    selectPlatform: "플랫폼 선택",
    selectPost: "게시물 선택",
    comments: "댓글",
    runComparison: "비교 분석 시작",
    analyzing: "분석 중...",
    autoMatch: "자동 매칭",
    autoMatchDesc: "제목 유사도 기반으로 다른 플랫폼의 유사 게시물을 자동 추천합니다",
    findMatches: "매칭 찾기",
    finding: "찾는 중...",
    matchFound: "매칭",
    similarity: "유사도",
    matchedKeywords: "공통 키워드",
    noMatches: "매칭되는 콘텐츠를 찾을 수 없습니다",
    manualSelect: "수동 선택",
    manualSelectDesc: "직접 두 플랫폼의 게시물을 선택하여 비교하세요",
    samePlatformError: "서로 다른 플랫폼을 선택해주세요",
    totalComments: "총 분석 댓글",
    sentiment: "감정",
    positive: "긍정",
    neutral: "중립",
    negative: "부정",
    sentimentChart: "감정 분포 비교",
    keywordComparison: "주요 키워드 비교",
    commonKeywords: "공통 키워드",
    uniqueKeywords: "고유 키워드",
    noCommonKeywords: "공통 키워드 없음",
    aiInsight: "AI 인사이트",
    detailAnalysis: "상세 분석",
    analyzedComments: "분석 댓글",
    topKeywords: "Top 키워드",
    analysisTime: "분석 시간",
    emptyState: "두 플랫폼의 게시물을 선택하고 비교 분석을 시작하세요",
    positiveLabel: "긍정적",
    negativeLabel: "부정적",
    neutralLabel: "중립",
    samePlatformNote: "같은 플랫폼 내 게시물 비교 모드",
    metricsTab: "성과 지표 비교",
    sentimentTab: "댓글 감정 비교",
    views: "조회수",
    likes: "좋아요",
    commentsMetric: "댓글",
    shares: "공유",
    saves: "저장",
    engagementRate: "참여율",
    post1Label: "게시물 A",
    post2Label: "게시물 B",
    metricsCompare: "성과 지표",
    diff: "차이",
  },
  en: {
    title: "Cross-Platform Comparison",
    subtitle: "Compare audience reactions to similar content across different platforms",
    platformA: "Platform A",
    platformB: "Platform B",
    selectPlatform: "Select Platform",
    selectPost: "Select Post",
    comments: "comments",
    runComparison: "Run Comparison",
    analyzing: "Analyzing...",
    autoMatch: "Auto Match",
    autoMatchDesc: "Automatically recommend similar posts from different platforms based on title similarity",
    findMatches: "Find Matches",
    finding: "Finding...",
    matchFound: "Match",
    similarity: "Similarity",
    matchedKeywords: "Common Keywords",
    noMatches: "No matching content found",
    manualSelect: "Manual Select",
    manualSelectDesc: "Select posts from two platforms to compare manually",
    samePlatformError: "Please select different platforms",
    totalComments: "Total Comments Analyzed",
    sentiment: "Sentiment",
    positive: "Positive",
    neutral: "Neutral",
    negative: "Negative",
    sentimentChart: "Sentiment Distribution Comparison",
    keywordComparison: "Keyword Comparison",
    commonKeywords: "Common Keywords",
    uniqueKeywords: "Unique Keywords",
    noCommonKeywords: "No common keywords",
    aiInsight: "AI Insight",
    detailAnalysis: "Detailed Analysis",
    analyzedComments: "Analyzed Comments",
    topKeywords: "Top Keywords",
    analysisTime: "Analysis Time",
    emptyState: "Select posts from two platforms and start comparison",
    positiveLabel: "Positive",
    negativeLabel: "Negative",
    neutralLabel: "Neutral",
    samePlatformNote: "Same platform post comparison mode",
    metricsTab: "Performance Metrics",
    sentimentTab: "Comment Sentiment",
    views: "Views",
    likes: "Likes",
    commentsMetric: "Comments",
    shares: "Shares",
    saves: "Saves",
    engagementRate: "Engagement Rate",
    post1Label: "Post A",
    post2Label: "Post B",
    metricsCompare: "Performance Metrics",
    diff: "Diff",
  }
};

const CrossPlatformComparison = ({ lang = "ko" }) => {
  const t = T[lang] || T.en;
  const [loading, setLoading] = useState(false);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingAutoMatch, setLoadingAutoMatch] = useState(false);
  const [matchablePosts, setMatchablePosts] = useState({ platform1: { posts: [] }, platform2: { posts: [] } });
  const [autoMatches, setAutoMatches] = useState([]);
  const [selectedPost1, setSelectedPost1] = useState("");
  const [selectedPost2, setSelectedPost2] = useState("");
  const [platform1, setPlatform1] = useState("youtube");
  const [platform2, setPlatform2] = useState("tiktok");
  const [comparison, setComparison] = useState(null);
  const [metricsComparison, setMetricsComparison] = useState(null);
  const [error, setError] = useState(null);
  const [showAutoMatch, setShowAutoMatch] = useState(false);
  const [activeTab, setActiveTab] = useState("metrics");

  const isSamePlatform = platform1 === platform2;

  // Fetch matchable posts when platforms change
  useEffect(() => {
    const fetchMatchablePosts = async () => {
      setLoadingPosts(true);
      try {
        const response = await axios.get(`${API}/posts/matchable`, {
          params: { platform1, platform2, limit: 20 }
        });
        setMatchablePosts(response.data);
        if (response.data.platform1?.posts?.length > 0) {
          setSelectedPost1(response.data.platform1.posts[0].post_id);
        } else {
          setSelectedPost1("");
        }
        if (response.data.platform2?.posts?.length > 0) {
          setSelectedPost2(response.data.platform2.posts[0].post_id);
        } else {
          setSelectedPost2("");
        }
        setAutoMatches([]);
        setShowAutoMatch(false);
      } catch (err) {
        console.error("Failed to fetch matchable posts:", err);
      } finally {
        setLoadingPosts(false);
      }
    };
    fetchMatchablePosts();
  }, [platform1, platform2]);

  const runAutoMatch = async () => {
    setLoadingAutoMatch(true);
    try {
      const response = await axios.get(`${API}/posts/auto-match`, {
        params: { platform1, platform2, limit: 5 }
      });
      setAutoMatches(response.data.matches || []);
      setShowAutoMatch(true);
      if (response.data.matches?.length > 0) {
        const bestMatch = response.data.matches[0];
        setSelectedPost1(bestMatch.post1.post_id);
        setSelectedPost2(bestMatch.post2.post_id);
      }
    } catch (err) {
      console.error("Auto-match failed:", err);
      setAutoMatches([]);
      setShowAutoMatch(true);
    } finally {
      setLoadingAutoMatch(false);
    }
  };

  const applyMatch = (match) => {
    // Inject matched posts into matchablePosts so Select dropdowns reflect them
    setMatchablePosts(prev => {
      const p1Posts = prev.platform1?.posts || [];
      const p2Posts = prev.platform2?.posts || [];
      const hasP1 = p1Posts.some(p => p.post_id === match.post1.post_id);
      const hasP2 = p2Posts.some(p => p.post_id === match.post2.post_id);
      return {
        platform1: { posts: hasP1 ? p1Posts : [{ post_id: match.post1.post_id, title: match.post1.title, comments: match.post1.comments || 0 }, ...p1Posts] },
        platform2: { posts: hasP2 ? p2Posts : [{ post_id: match.post2.post_id, title: match.post2.title, comments: match.post2.comments || 0 }, ...p2Posts] },
      };
    });
    setSelectedPost1(match.post1.post_id);
    setSelectedPost2(match.post2.post_id);
    // Scroll to comparison button
    setTimeout(() => {
      document.getElementById("run-comparison-area")?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
  };

  const runComparison = async () => {
    if (!selectedPost1 || !selectedPost2) return;
    if (isSamePlatform && selectedPost1 === selectedPost2) return;
    setLoading(true);
    setError(null);
    setMetricsComparison(null);
    setComparison(null);

    try {
      // Always fetch metrics comparison
      const post1 = matchablePosts.platform1?.posts?.find(p => p.post_id === selectedPost1);
      const post2 = matchablePosts.platform2?.posts?.find(p => p.post_id === selectedPost2);
      if (post1 && post2) {
        setMetricsComparison({ post1, post2 });
      }

      // Always fetch sentiment comparison (now supports same-platform too)
      const response = await axios.post(`${API}/comments/cross-platform-compare`, {
        platform1,
        post_id1: selectedPost1,
        platform2,
        post_id2: selectedPost2,
        max_comments: 100
      });
      setComparison(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "비교 분석에 실패했습니다.");
    } finally {
      setLoading(false);
    }
  };

  // Handle platform change: if user selects the same platform, swap them
  const handlePlatform1Change = (val) => {
    setPlatform1(val);
    setComparison(null);
  };

  const handlePlatform2Change = (val) => {
    setPlatform2(val);
    setComparison(null);
  };

  const sentimentLabel1 = isSamePlatform ? t.post1Label : platform1;
  const sentimentLabel2 = isSamePlatform ? t.post2Label : platform2;

  const sentimentChartData = comparison ? [
    { name: t.positive, [sentimentLabel1]: comparison.platform1.sentiment.positive, [sentimentLabel2]: comparison.platform2.sentiment.positive },
    { name: t.neutral, [sentimentLabel1]: comparison.platform1.sentiment.neutral, [sentimentLabel2]: comparison.platform2.sentiment.neutral },
    { name: t.negative, [sentimentLabel1]: comparison.platform1.sentiment.negative, [sentimentLabel2]: comparison.platform2.sentiment.negative },
  ] : [];

  const getSentimentLabel = (s) => s === "positive" ? t.positiveLabel : s === "negative" ? t.negativeLabel : t.neutralLabel;
  const getSentimentColor = (s) => s === "positive" ? "text-emerald-500" : s === "negative" ? "text-red-500" : "text-gray-400";

  return (
    <div className="space-y-6" data-testid="cross-platform-page">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <Globe className="h-6 w-6 text-cyan-400" />
        <div className="min-w-0 flex-1">
          <h2 className="text-xl font-heading font-bold">{t.title}</h2>
          <p className="text-sm text-muted-foreground">{t.subtitle}</p>
        </div>
      </div>

      {/* Auto-Match Section */}
      <Card className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-purple-500/20">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <Wand2 className="h-5 w-5 text-purple-400" />
              <CardTitle className="text-base">{t.autoMatch}</CardTitle>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={runAutoMatch}
              disabled={loadingAutoMatch || loadingPosts}
              className="w-full border-purple-500/30 hover:bg-purple-500/10 sm:w-auto"
              data-testid="auto-match-btn"
            >
              {loadingAutoMatch ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t.finding}</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" />{t.findMatches}</>
              )}
            </Button>
          </div>
          <CardDescription>{t.autoMatchDesc}</CardDescription>
        </CardHeader>
        
        {showAutoMatch && (
          <CardContent className="pt-0">
            {autoMatches.length > 0 ? (
              <div className="space-y-3">
                {autoMatches.map((match, idx) => {
                  const isSelected = selectedPost1 === match.post1.post_id && selectedPost2 === match.post2.post_id;
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border transition-all cursor-pointer ${
                        isSelected ? 'bg-purple-500/20 border-purple-500/50' : 'bg-muted/30 border-border/30 hover:bg-muted/50'
                      }`}
                      onClick={() => applyMatch(match)}
                      data-testid={`auto-match-item-${idx}`}
                    >
                      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className={`h-4 w-4 ${isSelected ? 'text-purple-400' : 'text-muted-foreground'}`} />
                          <span className="text-sm font-medium">{t.matchFound} #{idx + 1}</span>
                        </div>
                        <Badge variant="outline" className={`text-xs ${
                          match.similarity_score >= 70 ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                          : match.similarity_score >= 50 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                          : 'bg-muted text-muted-foreground'
                        }`}>
                          {t.similarity}: {match.similarity_score}%
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
                        <div className="flex min-w-0 items-start gap-2">
                          <Badge variant="outline" className={`${getPlatformBadgeClass(match.post1.platform)} shrink-0 capitalize`}>
                            {match.post1.platform}
                          </Badge>
                          <span className="truncate text-foreground">{match.post1.title}</span>
                        </div>
                        <div className="flex min-w-0 items-start gap-2">
                          <Badge variant="outline" className={`${getPlatformBadgeClass(match.post2.platform)} shrink-0 capitalize`}>
                            {match.post2.platform}
                          </Badge>
                          <span className="truncate text-foreground">{match.post2.title}</span>
                        </div>
                      </div>
                      {match.matched_keywords?.length > 0 && (
                        <div className="mt-2 flex items-center gap-1 flex-wrap">
                          <Link2 className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{t.matchedKeywords}:</span>
                          {match.matched_keywords.slice(0, 5).map((kw, i) => (
                            <Badge key={i} variant="outline" className="text-xs bg-cyan-500/10 text-cyan-400 border-cyan-500/20">
                              {kw}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <Alert className="bg-muted/30 border-border/30">
                <AlertDescription className="text-muted-foreground text-sm">{t.noMatches}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        )}
      </Card>

      {/* Manual Selection */}
      <Card className="bg-card/50 border-border/50">
        <CardHeader>
          <CardTitle className="text-base">{t.manualSelect}</CardTitle>
          <CardDescription>{t.manualSelectDesc}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
            {/* Platform A */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge className={`${getPlatformBadgeClass(platform1)} capitalize`}>{platform1}</Badge>
                <span className="text-sm text-muted-foreground">{t.platformA}</span>
              </div>
              <Select value={platform1} onValueChange={handlePlatform1Change}>
                <SelectTrigger data-testid="platform1-select">
                  <SelectValue placeholder={t.selectPlatform} />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedPost1} onValueChange={setSelectedPost1} disabled={loadingPosts}>
                <SelectTrigger data-testid="post1-select">
                  <SelectValue placeholder={t.selectPost} />
                </SelectTrigger>
                <SelectContent>
                  {matchablePosts.platform1?.posts?.map(post => (
                    <SelectItem key={post.post_id} value={post.post_id}>
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[200px]">{post.title || "Untitled"}</span>
                        <Badge variant="outline" className="text-xs shrink-0">{post.comments} {t.comments}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Divider with swap arrow */}
            <div className="hidden md:flex items-center justify-center pt-10">
              <ArrowLeftRight className="h-5 w-5 text-muted-foreground" />
            </div>

            {/* Platform B */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Badge className={`${getPlatformBadgeClass(platform2)} capitalize`}>{platform2}</Badge>
                <span className="text-sm text-muted-foreground">{t.platformB}</span>
              </div>
              <Select value={platform2} onValueChange={handlePlatform2Change}>
                <SelectTrigger data-testid="platform2-select">
                  <SelectValue placeholder={t.selectPlatform} />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedPost2} onValueChange={setSelectedPost2} disabled={loadingPosts}>
                <SelectTrigger data-testid="post2-select">
                  <SelectValue placeholder={t.selectPost} />
                </SelectTrigger>
                <SelectContent>
                  {matchablePosts.platform2?.posts?.map(post => (
                    <SelectItem key={post.post_id} value={post.post_id}>
                      <div className="flex items-center gap-2">
                        <span className="truncate max-w-[200px]">{post.title || "Untitled"}</span>
                        <Badge variant="outline" className="text-xs shrink-0">{post.comments} {t.comments}</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-6 flex flex-col items-center gap-3" id="run-comparison-area">
            {/* Show selected match info */}
            {selectedPost1 && selectedPost2 && (
              <div className="flex flex-wrap items-center justify-center gap-2 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                <Badge className={`${getPlatformBadgeClass(platform1)} capitalize text-xs`}>{platform1}</Badge>
                <span className="max-w-full truncate sm:max-w-[180px]">
                  {matchablePosts.platform1?.posts?.find(p => p.post_id === selectedPost1)?.title || selectedPost1.slice(0, 15)}
                </span>
                <ArrowLeftRight className="h-3 w-3" />
                <Badge className={`${getPlatformBadgeClass(platform2)} capitalize text-xs`}>{platform2}</Badge>
                <span className="max-w-full truncate sm:max-w-[180px]">
                  {matchablePosts.platform2?.posts?.find(p => p.post_id === selectedPost2)?.title || selectedPost2.slice(0, 15)}
                </span>
              </div>
            )}
            <Button
              onClick={runComparison}
              disabled={loading || !selectedPost1 || !selectedPost2 || (isSamePlatform && selectedPost1 === selectedPost2)}
              className="primary-glow w-full sm:min-w-[200px] sm:w-auto"
              data-testid="run-comparison-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t.analyzing}</>
              ) : (
                <><BarChart3 className="h-4 w-4 mr-2" />{t.runComparison}</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400" data-testid="comparison-error">
          {error}
        </div>
      )}

      {/* Same platform note */}
      {isSamePlatform && (metricsComparison || comparison) && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-500/20 bg-blue-500/10 p-3 text-sm text-blue-400">
          <BarChart3 className="h-4 w-4" />
          {t.samePlatformNote}
        </div>
      )}

      {/* Comparison Results */}
      {(metricsComparison || comparison) && (
        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="comparison-results">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 rounded-2xl bg-muted/30 p-1">
            <TabsTrigger value="metrics" className="shrink-0 whitespace-nowrap" data-testid="metrics-tab">{t.metricsTab}</TabsTrigger>
            <TabsTrigger value="sentiment" className="shrink-0 whitespace-nowrap" data-testid="sentiment-tab">{t.sentimentTab}</TabsTrigger>
          </TabsList>

          {/* Metrics Tab */}
          <TabsContent value="metrics">
            {metricsComparison && (
              <div className="space-y-6">
                {/* Bar Chart Comparison */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      {t.metricsCompare}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={[
                        { name: t.views, [t.post1Label]: metricsComparison.post1.views || 0, [t.post2Label]: metricsComparison.post2.views || 0 },
                        { name: t.likes, [t.post1Label]: metricsComparison.post1.likes || 0, [t.post2Label]: metricsComparison.post2.likes || 0 },
                        { name: t.commentsMetric, [t.post1Label]: metricsComparison.post1.comments || 0, [t.post2Label]: metricsComparison.post2.comments || 0 },
                        { name: t.shares, [t.post1Label]: metricsComparison.post1.shares || 0, [t.post2Label]: metricsComparison.post2.shares || 0 },
                        { name: t.saves, [t.post1Label]: metricsComparison.post1.saves || 0, [t.post2Label]: metricsComparison.post2.saves || 0 },
                      ]}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
                        <Tooltip contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: 12 }} />
                        <Legend />
                        <Bar dataKey={t.post1Label} fill={PLATFORM_COLORS[platform1] || "#3B82F6"} radius={[4, 4, 0, 0]} />
                        <Bar dataKey={t.post2Label} fill={isSamePlatform ? "#10B981" : (PLATFORM_COLORS[platform2] || "#EF4444")} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Detail Table */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    { post: metricsComparison.post1, label: t.post1Label, platform: platform1 },
                    { post: metricsComparison.post2, label: t.post2Label, platform: platform2 },
                  ].map(({ post, label, platform }, idx) => (
                    <Card key={idx} className="bg-card/50 border-border/50">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] uppercase" style={{ borderColor: PLATFORM_COLORS[platform], color: PLATFORM_COLORS[platform] }}>
                            {platform}
                          </Badge>
                          <span className="text-sm font-medium truncate">{post.title || "Untitled"}</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground/60 font-mono">{label} · {post.published_at?.slice(0, 10)}</p>
                      </CardHeader>
                      <CardContent className="space-y-2">
                        {[
                          { label: t.views, val: post.views },
                          { label: t.likes, val: post.likes },
                          { label: t.commentsMetric, val: post.comments },
                          { label: t.shares, val: post.shares },
                          { label: t.saves, val: post.saves },
                          { label: t.engagementRate, val: post.engagement_rate, suffix: "%" },
                        ].map((m) => (
                          <div key={m.label} className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">{m.label}</span>
                            <span className="font-mono font-medium">
                              {m.suffix ? `${(m.val || 0).toFixed(2)}${m.suffix}` : (m.val || 0).toLocaleString()}
                            </span>
                          </div>
                        ))}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Sentiment Tab */}
          {comparison && (
            <TabsContent value="sentiment">
              <div className="space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="bg-card/50 border-border/50">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{t.totalComments}</p>
                          <p className="text-2xl font-bold font-mono">
                            {comparison.platform1.total_comments + comparison.platform2.total_comments}
                          </p>
                        </div>
                        <MessageSquare className="h-8 w-8 text-muted-foreground/50" />
                      </div>
                    </CardContent>
                  </Card>
                  {[
                    { data: comparison.platform1, label: sentimentLabel1, platform: platform1 },
                    { data: comparison.platform2, label: sentimentLabel2, platform: platform2 }
                  ].map(({ data, label, platform }, idx) => (
                    <Card key={idx} className="bg-card/50 border-border/50">
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-muted-foreground">{label} {t.sentiment}</p>
                            <p className={`text-2xl font-bold capitalize ${getSentimentColor(data.overall_sentiment)}`}>
                              {getSentimentLabel(data.overall_sentiment)}
                            </p>
                            {data.title && <p className="text-xs text-muted-foreground mt-1 truncate max-w-[200px]">{data.title}</p>}
                          </div>
                          <Badge className={`${getPlatformBadgeClass(platform)} capitalize`}>{label}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Sentiment Chart */}
                <Card className="bg-card/50 border-border/50">
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-cyan-400" />
                      {t.sentimentChart}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={sentimentChartData} layout="vertical">
                        <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category" dataKey="name" width={60} />
                        <Tooltip
                          formatter={(value) => [`${value}%`]}
                          contentStyle={{ backgroundColor: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: "8px" }}
                        />
                        <Legend />
                        <Bar dataKey={sentimentLabel1} fill={PLATFORM_COLORS[platform1] || "#3B82F6"} name={sentimentLabel1.toUpperCase()} radius={[0, 4, 4, 0]} />
                        <Bar dataKey={sentimentLabel2} fill={isSamePlatform ? "#10B981" : (PLATFORM_COLORS[platform2] || "#EF4444")} name={sentimentLabel2.toUpperCase()} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                {/* Keywords + AI Insight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-card/50 border-border/50">
                    <CardHeader>
                      <CardTitle className="text-base">{t.keywordComparison}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">{t.commonKeywords}</p>
                        <div className="flex flex-wrap gap-2">
                          {comparison.comparison.common_keywords.length > 0 ? (
                            comparison.comparison.common_keywords.map((kw, i) => (
                              <Badge key={i} variant="outline" className="bg-purple-500/10 text-purple-400 border-purple-500/20">{kw}</Badge>
                            ))
                          ) : (
                            <span className="text-sm text-muted-foreground">{t.noCommonKeywords}</span>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">{sentimentLabel1} {t.uniqueKeywords}</p>
                        <div className="flex flex-wrap gap-2">
                          {comparison.comparison.unique_keywords_platform1.slice(0, 5).map((kw, i) => (
                            <Badge key={i} variant="outline" className={`${getPlatformBadgeClass(platform1)} text-xs`}>{kw}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground mb-2">{sentimentLabel2} {t.uniqueKeywords}</p>
                        <div className="flex flex-wrap gap-2">
                          {comparison.comparison.unique_keywords_platform2.slice(0, 5).map((kw, i) => (
                            <Badge key={i} variant="outline" className={`${getPlatformBadgeClass(platform2)} text-xs`}>{kw}</Badge>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 border-cyan-500/20">
                    <CardHeader>
                      <CardTitle className="text-base flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-cyan-400" />
                        {t.aiInsight}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm leading-relaxed">{comparison.ai_insight}</p>
                      <div className="mt-4 pt-4 border-t border-border/50">
                        <p className="text-xs text-muted-foreground">
                          {t.analysisTime}: {new Date(comparison.analyzed_at).toLocaleString("ko-KR")}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
      )}

      {/* Empty State */}
      {!comparison && !metricsComparison && !loading && (
        <Card className="bg-card/30 border-dashed border-border/50">
          <CardContent className="py-12 text-center">
            <Globe className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
            <p className="text-muted-foreground">{t.emptyState}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CrossPlatformComparison;
